import React from 'react';

import { isAllDay, showDateNoTime, showDateNoTimeExtended, showTime } from '@libs/NwMoment';
import { getTransportIcon } from '@libs/NwUtils';

import { EventDetailsWrapper } from '@components/EventDrawer/EventDrawerUi/EventUI';
import FlexContainer from '@components/Gui/FlexContainer';
import NwEllipsis from '@components/Gui/NwEllipsis';
import NwIcon from '@components/Gui/NwIcon';
import ResponsiveFlexContainer from '@components/Gui/ResponsiveFlexContainer';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const TravelDetails = ({ travelsData }) => {
    const getTravelPeriod = () => {
        if (showDateNoTime(travelsData.StartDateTime) !== showDateNoTime(travelsData.ArrivalDateTime)) {
            return (
                <FlexContainer mg="8px 0 0">
                    <div>
                        <div className="is-label">DEPARTURE</div>
                        <div className="is-content date-time">{showDateNoTime(travelsData.StartDateTime)}</div>
                        {!isAllDay(travelsData.StartDateTime, travelsData.ArrivalDateTime) && (
                            <div className="is-content date-time">{showTime(travelsData.StartDateTime)}</div>
                        )}
                        {travelsData.StartLocation && (
                            <div className="is-content travel-place">
                                <strong>{travelsData.StartLocation}</strong>
                            </div>
                        )}
                    </div>
                    <NwIcon className="arrow-icon" icon={byPrefixAndName.fasl['arrow-right']} />
                    <div>
                        <div className="is-label">ARRIVAL</div>
                        <div className="is-content date-time">{showDateNoTime(travelsData.ArrivalDateTime)}</div>
                        {!isAllDay(travelsData.StartDateTime, travelsData.ArrivalDateTime) && (
                            <div className="is-content date-time">{showTime(travelsData.ArrivalDateTime)}</div>
                        )}
                        {travelsData.ArrivalLocation && (
                            <div className="is-content travel-place">
                                <strong>{travelsData.ArrivalLocation}</strong>
                            </div>
                        )}
                    </div>
                </FlexContainer>
            );
        }
        if (isAllDay(travelsData.StartDateTime, travelsData.ArrivalDateTime)) {
            return (
                <>
                    <div className="date">{showDateNoTimeExtended(travelsData.StartDateTime)}</div>
                    <div className="is-label">ALL DAY</div>
                </>
            );
        }
        return (
            <>
                <div className="date">{showDateNoTimeExtended(travelsData.StartDateTime)}</div>
                <FlexContainer mg="8px 0 0">
                    <div>
                        <div className="is-label">DEPARTURE</div>
                        <div className="is-content date-time">{showTime(travelsData.StartDateTime)}</div>
                        {travelsData.StartLocation && (
                            <div className="is-content travel-place">
                                <strong>{travelsData.StartLocation}</strong>
                            </div>
                        )}
                    </div>
                    <NwIcon className="arrow-icon" icon={byPrefixAndName.fasl['arrow-right']} />
                    <div>
                        <div className="is-label">ARRIVAL</div>
                        <div className="is-content date-time">{showTime(travelsData.ArrivalDateTime)}</div>
                        {travelsData.ArrivalLocation && (
                            <div className="is-content travel-place">
                                <strong>{travelsData.ArrivalLocation}</strong>
                            </div>
                        )}
                    </div>
                </FlexContainer>
            </>
        );
    };

    const isGloballyCancelled = () => {
        if (travelsData.Cancel) {
            if (travelsData.RelatedEvents && travelsData.RelatedEvents.length > 0) {
                if (travelsData.RelatedEvents.every((e) => e.Cancel)) {
                    return true;
                } else {
                    return false;
                }
            }
            return true;
        }
        return false;
    };

    return (
        <EventDetailsWrapper className={isGloballyCancelled() ? 'is-cancelled' : null}>
            <ResponsiveFlexContainer
                leftMinWidth={180}
                columns={[
                    {
                        content: (
                            <div className="is-icon">
                                <NwIcon icon={getTransportIcon(travelsData.Transport)} />
                                <p className="is-label">{travelsData.Transport}</p>
                            </div>
                        ),
                        isBorder: true,
                    },
                    {
                        content: (
                            <>
                                {travelsData.Service && (
                                    <>
                                        <p className="is-label">SERVICE</p>
                                        <p className="is-content">{travelsData.Service.Name}</p>
                                        <br />
                                    </>
                                )}
                                {getTravelPeriod()}
                            </>
                        ),
                        isBorder: false,
                    },
                ]}
            />
            {!travelsData.GroupGuid && (
                <>
                    <ResponsiveFlexContainer
                        leftMinWidth={180}
                        columns={[
                            {
                                content: <p className="is-label">Ticket Number</p>,
                                isBorder: true,
                            },
                            {
                                content: <p className="is-content">{travelsData.TicketID}</p>,
                                isBorder: false,
                            },
                        ]}
                    />
                    <ResponsiveFlexContainer
                        leftMinWidth={180}
                        columns={[
                            {
                                content: <p className="is-label">Booking Reference</p>,
                                isBorder: true,
                            },
                            {
                                content: <p className="is-content">{travelsData.BookingReference}</p>,
                                isBorder: false,
                            },
                        ]}
                    />
                    <ResponsiveFlexContainer
                        leftMinWidth={180}
                        columns={[
                            {
                                content: <p className="is-label">Flight/Train Code</p>,
                                isBorder: true,
                            },
                            {
                                content:
                                    travelsData.Transport === 'Plane' && travelsData.TransportReference ? (
                                        <p className="is-content">
                                            {travelsData.TransportReference}
                                            <NwIcon
                                                style={{ marginLeft: '1rem' }}
                                                icon={byPrefixAndName.fass['plane-circle-check']}
                                                pointer
                                                tooltip="View flight details on Google"
                                                onClick={() =>
                                                    window.open(
                                                        `https://www.google.com/search?q=${travelsData.TransportReference}`,
                                                        '_blank'
                                                    )
                                                }
                                            />
                                        </p>
                                    ) : (
                                        <p className="is-content">{travelsData.TransportReference}</p>
                                    ),
                                isBorder: false,
                            },
                        ]}
                    />
                    <ResponsiveFlexContainer
                        leftMinWidth={180}
                        columns={[
                            {
                                content: <p className="is-label">Description</p>,
                                isBorder: true,
                            },
                            {
                                content: (
                                    <div className="is-content small">
                                        <NwEllipsis rows={3} text={travelsData.Description} />
                                    </div>
                                ),
                                isBorder: false,
                            },
                        ]}
                    />
                </>
            )}
        </EventDetailsWrapper>
    );
};

export default TravelDetails;
